// SPDX-License-Identifier: Apache-2.0
import IGirafeContext from '../../context/icontext';
import { IBrainSerializer } from '../../state/brain/serialize';
import LayoutState, {
  CellContent,
  distributeCells,
  getDefaultSizes,
  getGridSize,
  isCellComponent,
  isLayoutType,
  LayoutType
} from '../../state/layout';

export default class LayoutSerializer implements IBrainSerializer<LayoutState> {
  private readonly context: IGirafeContext;

  public constructor(context: IGirafeContext) {
    this.context = context;
  }

  private get layout() {
    return this.context.stateManager.state.layout;
  }

  public brainSerialize(layout: LayoutState): string {
    return JSON.stringify({
      type: layout.type,
      cells: layout.cells,
      sizes: layout.sizes
    });
  }

  public brainDeserialize(str: string): void {
    const parsedLayout = JSON.parse(str) as Partial<LayoutState>;
    if (!isLayoutType(parsedLayout.type)) {
      console.warn(`${parsedLayout.type} is not a valid layout. The shared layout will be ignored.`);
      return;
    }
    this.applyLayout(parsedLayout.type, parsedLayout.cells, parsedLayout.sizes);
  }

  /**
   * Applies a layout to the state, ignoring the values that are not valid anymore.
   * A shared state can have been created by another version of the application,
   * in which other components or other layouts were available.
   */
  public applyLayout(type: LayoutType, cells?: CellContent[], sizes?: LayoutState['sizes']) {
    const layout = this.layout;
    layout.type = type;
    layout.cells = distributeCells(
      type,
      (cells ?? []).map((cell) => (isCellComponent(cell) ? cell : null))
    );
    layout.sizes = this.getValidSizes(type, sizes);
  }

  private getValidSizes(type: LayoutType, sizes?: LayoutState['sizes']) {
    const defaultSizes = getDefaultSizes(type);
    const { columns, rows } = getGridSize(type);
    if (sizes?.columns?.length !== columns || sizes?.rows?.length !== rows) {
      return defaultSizes;
    }
    return { columns: sizes.columns, rows: sizes.rows };
  }
}
